import { DOCUMENT } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Booking, IPayout } from '@luggagehero/shared/interfaces';
import { SharedBookingService } from '@luggagehero/shared/services/bookings';
import { SharedNotificationService } from '@luggagehero/shared/services/notification';
import { SharedPageTaggingService } from '@luggagehero/shared/services/page-tagging';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { BaseComponent } from '../../../core';
import { CurrencyService, InvoiceService, PayoutService, StorageManagerService } from '../../../services/index';
import { ManageableStorageLocation } from '../../../services/storage-manager/storage-location';
import { StringUtil } from '../../../utils';
import { DateUtil } from '../../../utils/date.util';

const SystemLaunchDate: Date = new Date(2017, 3, 1);

enum TransactionType {
  income = 0,
  payout = 1,
}
interface ITransaction<T> {
  id: string;
  date: Date;
  text: string;
  amount: number;
  currency: string;
  type: TransactionType;
  value: T;
}

@Component({ template: '' })
export abstract class ReportTransactionsBaseComponent extends BaseComponent implements OnInit, OnDestroy {
  public activeTab = '';
  public shops: ManageableStorageLocation[];
  public bookings: Booking[];
  public payouts: IPayout[];
  public visibleTransactions: ITransaction<unknown>[];
  public selectedCurrency: string;
  public isTestMode = false;
  public selectedShop: ManageableStorageLocation;
  public shopId: string;
  public minDate: Date;
  public maxDate: Date;

  private _from: Date;
  private _to: Date;
  private fromNum: number;
  private toNum: number;
  private _isLoading = false;

  private paramsSubscription: Subscription;

  constructor(
    private storageManager: StorageManagerService,
    private notify: SharedNotificationService,
    private bookingService: SharedBookingService,
    private payoutService: PayoutService,
    private invoiceService: InvoiceService,
    private currencyService: CurrencyService,
    private tag: SharedPageTaggingService,
    renderer: Renderer2,
    @Inject(DOCUMENT) doc,
    private translate: TranslateService,
    private route: ActivatedRoute,
    private cd: ChangeDetectorRef,
  ) {
    super();

    this.invoiceService.init(renderer, doc);
  }

  get isLoading(): boolean {
    return this._isLoading;
  }
  set isLoading(value: boolean) {
    this._isLoading = value;
    this.cd.markForCheck();
  }

  get from(): Date {
    return this._from;
  }
  set from(value: Date) {
    this._from = value;

    this.fromNum = DateUtil.dateToNumber(value);
    this.cd.markForCheck();
  }

  get to(): Date {
    return this._to;
  }
  set to(value: Date) {
    this._to = value;

    this.toNum = DateUtil.dateToNumber(value);
    this.cd.markForCheck();
  }

  get totalIncome(): number {
    if (!this.visibleTransactions) {
      return 0;
    }

    let sum = 0;

    this.visibleTransactions.forEach((transaction) => {
      if (transaction.type === TransactionType.income) {
        const booking = <Booking>transaction.value;
        const price = booking.price.final;

        let amount = price.total - (price.refund || 0) + (price.additionalCharge || 0);

        if (amount > 0 && this.selectedShop.splitStartupFee === false) {
          amount -= price.startupFee;
        }

        sum += this.currencyService.convert(amount, price.currency, this.shopCurrency);
      }
    });

    return sum;
  }

  get totalTransferred() {
    if (!this.visibleTransactions) {
      return 0;
    }
    let sum = 0;
    this.visibleTransactions.forEach((transaction) => {
      if (transaction.type === TransactionType.payout) {
        const payout = <IPayout>transaction.value;
        const payoutAmount = this.currencyService.convert(payout.amount, payout.currency, this.shopCurrency);
        sum += payoutAmount > 0 ? payoutAmount : 0;
      }
    });
    return Math.max(sum, 0);
  }

  // TODO: Make this dynamic, possibly based on settings in db
  get shopCurrency(): string {
    switch (this.selectedShop.address.countryCode.toLowerCase()) {
      case 'ca':
      case 'us':
        return 'usd';

      case 'gb':
      case 'uk':
        return 'gbp';

      // Fall through to default (euro)
      case 'dk':
      case 'pt':
      default:
        return 'eur';
    }
  }

  ngOnInit() {
    this.isLoading = true;

    this.minDate = SystemLaunchDate;
    this.maxDate = new Date();

    this.to = new Date(this.maxDate.getFullYear(), this.maxDate.getMonth(), this.maxDate.getDate());
    this.from = DateUtil.addDays(this.to, -90);

    void this.getShops();

    this.paramsSubscription = this.route.params.subscribe((params: { id: string; testMode: string }) => {
      this.shopId = params.id;
      if (params.testMode) {
        this.isTestMode = params.testMode === 'true';
      }
      void this.loadShopData(this.shopId);
    });

    this.tag.disableCrawling();
  }

  ngOnDestroy() {
    this.tag.clearTags();

    try {
      this.paramsSubscription.unsubscribe();
    } catch {
      // Ignore
    }
  }

  showPayments() {
    if (this.activeTab === 'payments') {
      return;
    }
    this.activeTab = 'payments';
    this.applyFilters();
  }

  showPayouts() {
    if (this.activeTab === 'payouts') {
      return;
    }
    this.activeTab = 'payouts';
    this.applyFilters();
  }

  async getShops() {
    try {
      this.shops = await this.storageManager.getStorageLocations();

      if (this.shops.length > 0) {
        this.selectedShop = this.shops.find((shop) => shop._id === this.shopId) || this.shops[0];
        this.selectedCurrency = this.shopCurrency;
      }
    } catch (err) {
      this.notify.error(StringUtil.errorToString(err));
    }
  }

  async loadShopData(shopId: string) {
    this.isLoading = true;

    try {
      // Payments tab is not shown any longer so we can disable this for now
      // this.bookings = await this.bookingService.getBookingsForStorageLocation(shopId, this.from, this.to);
      this.payouts = await this.payoutService.getPayouts(shopId, this.isTestMode);

      this.showPayouts();
    } catch (err) {
      this.notify.error(StringUtil.errorToString(err));
    }

    this.isLoading = false;
  }

  showInvoice(payout: IPayout) {
    this.invoiceService.open(payout, this.selectedShop);
  }

  downloadInvoice(payout: IPayout) {
    this.invoiceService.download(payout, this.selectedShop);
  }

  applyFilters() {
    // Clear visible transactions
    this.visibleTransactions = [];

    this.isLoading = true;

    // HACK: Ensure that the UI is updated to loading state
    setTimeout(() => {
      const transactions: ITransaction<unknown>[] = [];

      if (this.activeTab === 'payments' && this.bookings) {
        const visibleBookings = this.bookings.filter((b) => this.filterBooking(b));

        visibleBookings.forEach((booking) => {
          const textKey =
            booking.status === 'PAID'
              ? booking.paidDirectly
                ? 'DIRECT_PAYMENT_SELECTED'
                : 'ONLINE_PAYMENT_SUCCESSFUL'
              : 'ONLINE_PAYMENT_FAILED';

          const price = booking.price.final;

          let amount = price.total - (price.refund || 0) + (price.additionalCharge || 0);

          if (amount > 0) {
            if (this.selectedShop.splitStartupFee === false) {
              amount -= price.startupFee;
            }

            const bookingTransaction: ITransaction<Booking> = {
              type: TransactionType.income,
              id: booking._id,
              amount: amount,
              currency: price.currency,
              date: new Date(booking.period.checkOut),
              text: this.translate.instant(textKey) as string,
              value: booking,
            };
            transactions.push(bookingTransaction);
          }
        });
      }

      if (this.activeTab === 'payouts' && this.payouts) {
        const visiblePayouts = this.payouts.filter((b) => this.filterPayout(b));

        visiblePayouts.forEach((payout) => {
          const payoutTransaction: ITransaction<IPayout> = {
            type: TransactionType.payout,
            id: payout.key,
            amount: payout.amount,
            currency: payout.currency,
            date: new Date(payout.created),
            text: this.translate.instant('PAYOUT') as string,
            value: payout,
          };
          transactions.push(payoutTransaction);
        });
      }
      type WithDate = { date: { getTime: () => number } };
      transactions.sort((a: WithDate, b: WithDate) => a.date.getTime() - b.date.getTime());

      this.visibleTransactions = transactions;
      this.isLoading = false;
    }, 0);
  }

  filterBooking(booking: Booking): boolean {
    const checkOutDate = DateUtil.dateToNumber(booking.period.checkOut);
    if (checkOutDate < this.fromNum || checkOutDate > this.toNum) {
      return false;
    }
    return ['PAID', 'CHECKED_OUT'].includes(booking.status);
  }

  filterPayout(payout: IPayout): boolean {
    if (payout.testMode !== this.isTestMode) {
      return false;
    }
    const payoutDate = DateUtil.dateToNumber(payout.created);
    if (payoutDate < this.fromNum || payoutDate > this.toNum) {
      return false;
    }
    return true;
  }
}
